import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaEye,
  FaCheckCircle,
  FaEnvelope,
  FaPhone,
  FaUserCheck,
  FaWallet,
  FaCrown,
  FaChartLine,
} from "react-icons/fa";
import { ViewPlanlistByVendor } from "../../redux/services/SubscriptionServices/Subscription.Services";
import Pagination from "../../common/Pagination";
import { errorToast } from "../../Utils/toast";

function PlanlistByVendor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [tableLoading, setTableLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [subscription, setSubscription] = useState([]);
  const [vendorInfo, setVendorInfo] = useState(null);
  const [totalAmountPaid, setTotalAmountPaid] = useState(0);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  const fetchSubscriptionList = async (pageNo = 1) => {
    try {
      setTableLoading(true);
      const payload = { vendor_id: id, search, page: pageNo, per_page: limit };
      const res = await ViewPlanlistByVendor(payload);
      const list = Array.isArray(res?.subscriptions) ? res.subscriptions : [];
      setSubscription(list);
      setVendorInfo(res?.vendor || null);
      setTotalAmountPaid(res?.total_amount_paid ?? 0);
      setTotalSubscriptions(res?.total_subscriptions ?? 0);
      setPage(res?.current_page ?? 1);
      setTotalPages(res?.last_page ?? 1);
    } catch (error) {
      console.log(error);
      errorToast("Failed to load vendor plans");
      setSubscription([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchSubscriptionList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, limit, search]);

  // Active plans sabse upar
  const sortedSubscriptions = useMemo(() => {
    const isActive = (s) => String(s || "").toLowerCase() === "active";
    return [...subscription].sort((a, b) => {
      const aActive = isActive(a?.status);
      const bActive = isActive(b?.status);
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      return 0;
    });
  }, [subscription]);

  // Total AI Calls Usage Calculate
 const totalAiUsage = useMemo(() => {
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  return subscription.reduce(
    (acc, item) => {
      const ai = item?.usage?.ai_calls;
      if (!ai) return acc;

      const used = Math.max(toNum(ai.used), 0);
      const limitVal = Math.max(toNum(ai.limit), 0);

      acc.used += used;
      acc.limit += limitVal;
      acc.remaining += limitVal > 0 ? Math.max(limitVal - used, 0) : 0;

      return acc;
    },
    { used: 0, limit: 0, remaining: 0 }
  );
}, [subscription]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !tableLoading) {
      setPage(newPage);
      fetchSubscriptionList(newPage);
    }
  };

  const formatPrice = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    return `₹${Number(val).toLocaleString("en-IN")}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getSubStatusBadge = (status) => {
    if (!status) return "status-inactive";
    const key = String(status).toLowerCase();
    const map = {
      active: "status-active",
      inactive: "status-inactive",
      cancelled: "status-cancelled",
      canceled: "status-cancelled",
      expired: "status-expired",
      pending: "status-pending",
      approved: "status-active",
    };
    return map[key] || "status-inactive";
  };

  const getPaymentBadge = (status) => {
    if (!status) return "status-inactive";
    const key = String(status).toLowerCase();
    const map = {
      paid: "status-active",
      success: "status-active",
      pending: "status-pending",
      failed: "status-cancelled",
      unpaid: "status-pending",
    };
    return map[key] || "status-inactive";
  };

  return (
    <div className="planlist-page">
      {/* TOP BAR */}
      <div className="planlist-topbar d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="page-title mb-1">
            {vendorInfo?.business_name || "Vendor"} — Plans
          </h4>
          <div className="page-subtitle text-muted" style={{ fontSize: '13px' }}>
            <FaUserCheck size={11} /> {vendorInfo?.user_id || "VND-XXXX"}
          </div>
        </div>
        <button className="add-btn" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      {/* DASHBOARD ROW (Vendor Card + Stats) */}
      <div className="dashboard-row d-flex gap-4 mb-4" style={{ flexWrap: 'wrap' }}>
        
        {/* VENDOR CARD (Left side) */}
        {vendorInfo && (
          <div className="vendor-card" style={{ flex: '1 1 350px' }}>
            <div className="vendor-card-top d-flex align-items-center gap-3 mb-3">
              <div className="vendor-avatar-large">
                {vendorInfo.name?.charAt(0) || "V"}
              </div>
              <div className="vendor-info-main">
                <div className="vendor-name-large d-flex align-items-center gap-2">
                  <h5 className="mb-0">{vendorInfo.name || "-"}</h5>
                  {vendorInfo.kyc_status && (
                    <span className={`vendor-kyc ${getSubStatusBadge(vendorInfo.kyc_status)}`} style={{ fontSize: '10px', padding: '2px 8px' }}>
                      <FaUserCheck size={9} /> KYC {vendorInfo.kyc_status}
                    </span>
                  )}
                </div>
                <div className="vendor-business text-muted mt-1" style={{ fontSize: '13px' }}>
                  {vendorInfo.business_name || "-"}
                </div>
              </div>
            </div>

            <div className="vendor-contacts-grid d-flex gap-3 mt-4">
              <div className="vendor-contact-item flex-fill bg-dark p-3 rounded d-flex align-items-center gap-2">
                <FaEnvelope size={14} className="text-primary" />
                <div>
                  <div className="vc-label text-muted" style={{ fontSize: '10px', textTransform: 'uppercase' }}>EMAIL</div>
                  <div className="vc-value" style={{ fontSize: '13px' }}>{vendorInfo.email || "-"}</div>
                </div>
              </div>
              <div className="vendor-contact-item flex-fill bg-dark p-3 rounded d-flex align-items-center gap-2">
                <FaPhone size={14} className="text-primary" />
                <div>
                  <div className="vc-label text-muted" style={{ fontSize: '10px', textTransform: 'uppercase' }}>MOBILE</div>
                  <div className="vc-value" style={{ fontSize: '13px' }}>{vendorInfo.mobile || "-"}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STATS CARDS (Right side) */}
        <div className="stats-grid d-flex flex-wrap gap-3" style={{ flex: '1 1 500px' }}>
          
          {/* Amount Paid */}
          <div className="stat-box bg-dark p-4 rounded" style={{ flex: '1 1 30%', minWidth: '150px' }}>
            <div className="stat-icon stat-green mb-3">
              <FaWallet size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>TOTAL AMOUNT PAID</div>
              <div className="stat-value stat-value-green" style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {formatPrice(totalAmountPaid)}
              </div>
            </div>
          </div>

          {/* Total Subscriptions */}
          <div className="stat-box bg-dark p-4 rounded" style={{ flex: '1 1 30%', minWidth: '150px' }}>
            <div className="stat-icon stat-blue mb-3">
              <FaCrown size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>TOTAL SUBSCRIPTIONS</div>
              <div className="stat-value" style={{ fontSize: '24px', fontWeight: 'bold' }}>{totalSubscriptions}</div>
            </div>
          </div>

          {/* Total AI Calls Usage */}
          <div className="stat-box bg-dark p-4 rounded" style={{ flex: '1 1 30%', minWidth: '150px' }}>
            <div className="stat-icon stat-purple mb-3" style={{ color: '#8b5cf6' }}>
              <FaChartLine size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>TOTAL AI CALLS USAGE</div>
              <div className="stat-value" style={{ fontSize: '22px', fontWeight: 'bold' }}>
                {totalAiUsage.used.toLocaleString("en-IN")} / {totalAiUsage.limit.toLocaleString("en-IN")}
              </div>
              <div className="stat-sub text-muted mt-1" style={{ fontSize: '12px' }}>
                {totalAiUsage.remaining.toLocaleString("en-IN")} remaining
              </div>
            </div>
          </div>
          
        </div>
      </div>

      <div className="planlist-main">
        {tableLoading ? (
          <div className="loader-wrap text-center py-5">
            <div className="spinner-border text-primary"></div>
            <p className="mt-2">Loading plans...</p>
          </div>
        ) : sortedSubscriptions.length > 0 ? (
          <>
            <div className="plan-list">
              {sortedSubscriptions.map((item) => (
                <div className="plan-card-full" key={item.id}>
                  {/* LEFT: Main Info */}
                  <div className="plan-main">
                    <div className="plan-header">
                      <div className="plan-header-left">
                        <h4 className="plan-title">{item?.plan_name || "Untitled Plan"}</h4>
                        <div className="plan-tags mt-2">
                          {item?.module_name && (
                            <span className="plan-tag purple">{item.module_name}</span>
                          )}
                        </div>
                      </div>
                      <span className={`plan-status ${getSubStatusBadge(item?.status)}`}>
                        {item?.status || "-"}
                      </span>
                    </div>

                    <div className="plan-info-row">
                      <div className="plan-info-item">
                        <div className="plan-info-label">Amount Paid</div>
                        <div className="plan-info-value green">{formatPrice(item?.amount_paid)}</div>
                      </div>
                      <div className="plan-divider" />
                      <div className="plan-info-item">
                        <div className="plan-info-label">Payment</div>
                        <span className={`plan-payment ${getPaymentBadge(item?.payment_status)}`}>
                          {item?.payment_status || "-"}
                        </span>
                      </div>
                    </div>

                    <div className="plan-dates-row">
                      <div className="plan-date-item">
                        <div className="date-dot dot-green"></div>
                        <div>
                          <div className="plan-date-label">Start Date</div>
                          <div className="plan-date-value">{formatDate(item?.start_date)}</div>
                        </div>
                      </div>
                      <div className="plan-date-line">
                        <div className="date-line-progress" />
                      </div>
                      <div className="plan-date-item">
                        <div className="date-dot dot-red"></div>
                        <div>
                          <div className="plan-date-label">End Date</div>
                          <div className="plan-date-value">{formatDate(item?.end_date)}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: Sidebar */}
                  <div className="plan-side">
                    {item?.usage?.ai_calls && (
                      <div className="usage-block">
                        <div className="usage-header-row">
                          <FaChartLine size={13} className="usage-header-icon" />
                          <span>AI Calls Usage</span>
                        </div>
                        <div className="usage-numbers">
                          <div className="usage-big">
                            {item.usage.ai_calls.used}
                            <span className="usage-split">/{item.usage.ai_calls.limit}</span>
                          </div>
                          <div className="usage-percent">
                            {item.usage.ai_calls.limit > 0
                              ? Math.round((item.usage.ai_calls.used / item.usage.ai_calls.limit) * 100)
                              : 0}%
                          </div>
                        </div>
                        <div className="usage-bar">
                          <div
                            className="usage-fill"
                            style={{
                              width: `${item.usage.ai_calls.limit > 0 ? (item.usage.ai_calls.used / item.usage.ai_calls.limit) * 100 : 0}%`,
                            }}
                          />
                        </div>
                        <div className="usage-remaining">
                          <span className="dot-green-sm" />
                          {item.usage.ai_calls.remaining} remaining
                        </div>
                      </div>
                    )}

                    {item?.payment_method && (
                      <div className="payment-block">
                        <div className="payment-label">Payment Method</div>
                        <div className="payment-value">{item.payment_method}</div>
                      </div>
                    )}

                    <button
                      className="plan-btn"
                      onClick={() => navigate(`/view-assigned-plan/${item.id}`)}
                    >
                      <FaEye size={13} /> View Full Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-end mt-4">
                <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
            )}
          </>
        ) : (
          <div className="empty-state text-center py-5">
            <div className="empty-icon" style={{ fontSize: '40px' }}>📋</div>
            <div className="empty-title mt-3">No plans found</div>
            <div className="empty-sub text-muted">This vendor doesn't have any active plans yet.</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlanlistByVendor;